import { FC, useState } from 'react';
import { CustomInput } from 'components';
import { DropDownChevronDownIcon } from 'assets/icons';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import { REGEX } from 'constants/common';
import { getAmountFilterIcon } from '../utils';
import { PAYOUT_TXNS_AMOUNT_FILTERS, PAYOUT_TXNS_AMOUNT_FILTERS_ARRAY } from '../constants';
import { AmountFilterValue, AmountValue } from '../payouts.model';

interface AmountFilterBoxProps {
  selectedFilter: AmountFilterValue;
  onFilterChange: (filter: AmountFilterValue) => void;
  className?: string;
}

const AmountFilterBox: FC<AmountFilterBoxProps> = ({ selectedFilter, onFilterChange }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterSelected, setIsFilterSelected] = useState(selectedFilter.min !== '');
  const [amountValue, setAmountValue] = useState<AmountValue>({
    min: selectedFilter.min,
    max: selectedFilter.max
  });

  const handleFilterClick = (filter: typeof PAYOUT_TXNS_AMOUNT_FILTERS_ARRAY[0]) => {
    setIsFilterSelected(filter.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL);
    onFilterChange({
      title: filter.title,
      min: amountValue.min,
      max: amountValue.max
    });
    setIsFilterOpen(false);
  };

  const handleAmountValueChange = (value: string, type: 'min' | 'max') => {
    if (REGEX.amount.test(value)) {
      const updatedValue = { ...amountValue, [type]: value };
      setAmountValue(updatedValue);
      onFilterChange({
        title: selectedFilter.title,
        min: updatedValue.min,
        max: updatedValue.max
      });
    }
  };

  const closeAmountDropDown = () => {
    setIsFilterOpen(false);
  };

  const toggleAmountDropDown = () => {
    setIsFilterOpen(prev => !prev);
    setIsFilterSelected(false);
  };

  const renderFilterAnchor = () => {
    const { Icon } = getAmountFilterIcon(selectedFilter.title as PAYOUT_TXNS_AMOUNT_FILTERS);
    return (
      <div
        className={`mt-5 flex w-full cursor-pointer items-center justify-between rounded-md border
        ${isFilterOpen ? 'border-secondary' : 'border-borderGray'}  bg-primary p-3`}
        onClick={toggleAmountDropDown}>
        <div className="flex items-center gap-2">
          <Icon className="shrink-0" />
          <div className="text-sbase font-semibold text-primaryText">{selectedFilter.title}</div>
        </div>
        <div>
          <DropDownChevronDownIcon className={isFilterOpen ? 'rotate-180' : ''} />
        </div>
      </div>
    );
  };

  const renderFilterOptions = () => {
    return (
      <div
        className="customNormalScroll absolute w-full space-y-2.5 
        overflow-auto rounded-md border border-borderGray bg-primary py-5 px-4">
        {PAYOUT_TXNS_AMOUNT_FILTERS_ARRAY.map(filter => {
          const { Icon } = getAmountFilterIcon(filter.title);
          return (
            <div
              className="flex cursor-pointer items-center gap-2.5"
              key={filter.title}
              onClick={() => handleFilterClick(filter)}>
              <Icon className="shrink-0" />
              <div className="text-sbase text-headingGray">{filter.title}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative">
      <OutsideClickHandle onOutsideClick={closeAmountDropDown}>
        <>
          <div className={isFilterSelected ? 'rounded-b-md bg-secondaryBtn' : ''}>
            {renderFilterAnchor()}
            {isFilterSelected && selectedFilter.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL && (
              <div className="rounded-b-md px-5 pt-2 pb-4">
                {selectedFilter.title === PAYOUT_TXNS_AMOUNT_FILTERS.IN_BETWEEN ? (
                  <div className="flex items-center justify-between gap-2.5">
                    <div>
                      <div className="pb-1 text-sbase font-semibold text-primaryText">Min</div>
                      <CustomInput
                        id="amount-filter-min"
                        value={amountValue.min}
                        className="h-[30px] rounded-md border border-border bg-white outline-none"
                        onChange={val => handleAmountValueChange(val, 'min')}
                        prefix="$"
                        prefixWrapperStyle="left-1.5 text-primaryText font-semibold text-[15px]"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <div className="pb-1 text-sbase font-semibold text-primaryText">Max</div>
                      <CustomInput
                        id="amount-filter-max"
                        value={amountValue.max}
                        className="h-[30px] rounded-md border border-border bg-white outline-none"
                        onChange={val => handleAmountValueChange(val, 'max')}
                        prefix="$"
                        prefixWrapperStyle="left-1.5 text-primaryText font-semibold text-[15px]"
                        placeholder="Enter amount"
                      />
                    </div>
                  </div>
                ) : (
                  <CustomInput
                    id="amount-filter-single"
                    value={amountValue.min}
                    className="h-[30px] rounded-md border border-border bg-white outline-none"
                    onChange={val => handleAmountValueChange(val, 'min')}
                    prefix="$"
                    prefixWrapperStyle="left-1.5 text-primaryText font-semibold text-[15px]"
                    placeholder="Enter amount"
                  />
                )}
              </div>
            )}
          </div>
          {isFilterOpen && renderFilterOptions()}
        </>
      </OutsideClickHandle>
    </div>
  );
};

export default AmountFilterBox;
